<template>
  <div class="company" v-if="company.id">
    <div class="company__wrapper">
      <InternalHeader title="Карточка уровня"  class="company__header" :itemsBreadcrumbs="company.breadcrumbs" :tab="queryParams" forceBreadcrumbs/>
      <div class="company__block-wrapper">
        <div class="container">
          <DetailedCompanyCard class="company__card" :company="company"/>
        </div>
        <div class="company__block-menu">
          <div class="company__block-menu-title">Вы можете:</div>
          <router-link :to="`/companies/${company.id}/edit-company`" class="company__block-menu-btn">
              <Pencil />
              Редактировать
          </router-link>
        </div>
      </div>
    </div>

    <div class="company__employees">
        <div class="company__employees-header">
            <h2 class="company__employees-title">Доступы<span
                v-if="company.employees.length">{{ company.employees.length }}</span></h2>
            <VButtonNew
                label="Новый доступ"
                autoSize
                link
                style-type="link"
                :to="`/companies/${company.id}/create-employee`"
                class="company__employees-new">
                <template #iconLeft>
                  <IconsPlus />
                </template>
            </VButtonNew>
        </div>

        <div class="company__employees-cards">
            <div class="company__employees-cards-empty" v-if="!company.employees.length">
                <h3 class="company__employees-cards-empty-title">Список доступов пуст</h3>
                <p class="company__employees-cards-empty-description">В этом уровне пока нет доступов</p>
            </div>
            <EmployeeCard
                class="company__employees-card"
                v-for="employee in company.employees"
                :key="employee.id"
                :employee="employee"
                :companyId="company.id"/>
        </div>
    </div>

    <div class="company__employees">
      <div class="company__employees-header">
        <h2 class="company__employees-title">Уровни<span
            v-if="company.employees.length">{{ company.sub_companies.length }}</span></h2>
        <VButtonNew
            label="Новый уровень"
            autoSize
            link
            style-type="link"
            :to="`/companies/new${queryParams}`"
            class="company__employees-new">
          <template #iconLeft>
            <IconsPlus />
          </template>
        </VButtonNew>
      </div>

      <div class="company__employees-cards">
        <div class="company__employees-cards-empty" v-if="!company.sub_companies.length">
          <h3 class="company__employees-cards-empty-title">Список подконтрольных уровней пуст</h3>
          <p class="company__employees-cards-empty-description">В этом уровне пока нет подконтрольных уровней</p>
        </div>
        <CompanyCard
            class="company__employees-card"
            v-for="company in company.sub_companies"
            :key="company.id"
            :company="company"
        />
      </div>
    </div>
  </div>
  <div v-else>

    <div class="company__wrapper">
      <InternalHeader title="Карточка уровня" class="company__header"/>
      <div class="company__block-wrapper">
        <div class="container">
        <VSkeleton style="width: 832px" variants="CARD_CANDIDATE" v-if="skeleton"/>
        </div>
      </div>
    </div>
        <VSkeleton variants="CARD_COMPANY_FOOTER" v-if="skeleton"/>
  </div>
</template>

<script setup>
import {computed, onDeactivated, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import companies from "@/api/companies";

import InternalHeader from "@/components/InternalHeader";
import DetailedCompanyCard from "@/components/DetailedCompanyCard";
import EmployeeCard from "@/components/EmployeeCard";
import VSkeleton from "@/components/UI/VSkeleton/index.vue";
import CompanyCard from "@/components/CompanyCard/index.vue";
import IconsPlus from "@/components/Icons/Plus.vue";
import Pencil from "@/components/Icons/Pencil.vue";

const route = useRoute();
const skeleton = ref(true)

const company = ref({})
const getCompany = async () => {
    try {
        company.value = await companies.getCompany(route.params.id)
    } catch (error) {
        console.log(error)
    }
}

onMounted(() => {
    getCompany()
})

onDeactivated(() => {
    company.value = {}
})

const queryParams = computed(() => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(route.query)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  }
  return '?' + searchParams.toString() + `&company_id=${route.params.id}`
})
</script>

<style scoped lang="scss">
.company {
    padding-bottom: 43px;

    &::v-deep(.v-breadcrumbs__item) {
        &:nth-child(2) {
            img {
                display: none;
            }
        }
    }

    &__wrapper {
        padding-top: 12px;
    }

    &__block-wrapper {
        width: 100%;
        margin-top: 24px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    &__block-menu {
        width: 256px;
        padding: 24px 16px;
        border-radius: 16px;
        background-color: #fff;
        box-shadow: 2px 2px 8px 0px rgba(0, 0, 0, 0.04);
    }

    &__block-menu-title {
        padding-left: 8px;
        color: $dark;
        font-size: 14px;
        font-weight: 600;
    }

    &__block-menu-btn {
        height: 32px;
        padding: 0 8px;
        margin-top: 16px;
        display: flex;
        align-items: center;
        color: $blue;
        font-size: 14px;
        font-weight: 500;
        text-decoration: none;
        cursor: pointer;
        user-select: none;

        img {
            margin-right: 4px;
        }
    }

    &__employees {
        margin-top: 40px;
    }

    &__employees-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
    }

    &__employees-title {
        font-size: 24px;
        span {
            margin-left: 4px;
            color: $gray;
            font-size: 16px;
            font-weight: 700;
        }
    }

    &__employees-new {
        height: 32px;
        padding: 0 8px;
        font-size: 14px;
        font-weight: 500;

        &:active {
            background-color: $light-gray-2 !important;
        }
    }

    &__employees-cards {
        margin-left: -32px;
        display: flex;
        flex-wrap: wrap;
    }

    &__employees-cards-empty {
        width: 100%;
        margin-top: 207px;
        margin-bottom: 160px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    &__employees-cards-empty-title {
        font-size: 24px;
        font-weight: 600;
    }

    &__employees-cards-empty-description {
        margin-top: 12px;
        font-size: 16px;
        font-weight: 400;
    }

    &__employees-card {
        width: calc(33.333% - 32px);
        margin-top: 24px;
        margin-left: 32px;
    }
}
</style>
